<template>
    <div class="dashboard-container">
        <!-- Formulario de búsqueda -->
        <FormSearch @company-id-changed="handleCompanyIdChanged" @handle-loading="handleLoadingChanged" :loading="loading" />

        <!-- Esqueleto de carga -->
        <Dash1Skeleton v-if="loading" />

        <!-- Contenido principal -->
        <div v-if="selectedCompanyId && !loading">
            <!-- Componentes de pantalla -->
            <Health v-if="screens.health" />
            <General v-if="screens.accident" />
            <Usability v-if="screens.usability" />
        </div>

        <!-- Botón flotante -->
        <div class="floating-btn">
            <button type="button" 
                    class="btn btn-primary btn-circle btn-lg shadow-pulse" 
                    @click="toggleFloatingMenu"
                    :class="{ 'active': showFloatingMenu }"
                    data-toggle="tooltip" 
                    data-placement="left" 
                    title="Cambio de Pantalla">
                    <i class="fa fa-plus" aria-hidden="true"></i>
            </button>
            
            <!-- Menú flotante con transición -->
            <transition name="fade">
                <div v-if="showFloatingMenu" class="floating-menu">
                    <button type="button" 
                            class="btn btn-menu" 
                            @click="handleScreenChange('accident')"
                            v-if="validarPermisoComponente('ver-tablero-accidente')"
                            :class="{ 'active': screens.accident }">
                        <i class="fa fa-exclamation-triangle" aria-hidden="true"></i>
                        <span>General</span>
                    </button>
                    <button type="button" 
                            class="btn btn-menu" 
                            @click="handleScreenChange('health')"
                            v-if="validarPermisoComponente('ver-tablero-salud')"
                            :class="{ 'active': screens.health }">
                        <i class="fa fa-heart" aria-hidden="true"></i>
                        <span>Salud</span>
                    </button>
                    <button type="button" 
                            class="btn btn-menu" 
                            @click="handleScreenChange('usability')"
                            v-if="validarPermisoComponente('ver-tablero-usabilidad')"
                            :class="{ 'active': screens.usability }">
                        <i class="fa fa-table" aria-hidden="true"></i>
                        <span>Usabilidad</span>
                    </button>
                </div>
            </transition>
        </div>
    </div>
</template>

<script>
import { mapMutations, mapState, mapActions } from "vuex";
import Dash1Skeleton from "./components/skeleton/Dash1Skeleton.vue";
import FormSearch from "./components/FormSearch.vue";
import Health from "./screens/Health.vue";
import General from "./screens/General.vue";
import Usability from "./screens/Usability.vue";
import { validarPermisoComponente } from "../../plugins/acl";

export default {
    components: {
        FormSearch,
        Dash1Skeleton,
        Health,
        General,
        Usability
    },
    data() {
        return {
            selectedCompanyId: null,
            loading: false,
            showFloatingMenu: false,
            validarPermisoComponente: validarPermisoComponente,
        };
    },
    mounted() {
        this.load(false);
        // Cargar la fuente Poppins
        const link = document.createElement('link');
        link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700&display=swap';
        link.rel = 'stylesheet';
        document.head.appendChild(link);
        this.createUsabilityEvents('ingreso_modulo_dashboard');
    },
    beforeDestroy() {
        // Remover la fuente cuando el componente se destruye
        const links = document.head.getElementsByTagName('link');
        for (let i = 0; i < links.length; i++) {
            if (links[i].href.includes('fonts.googleapis.com/css2?family=Poppins')) {
                document.head.removeChild(links[i]);
                break;
            }
        }
        document.removeEventListener('click', this.handleClickOutside);
    },
    methods: {
        ...mapMutations("dashboard", ["load", "setScreenActive"]),
        handleCompanyIdChanged(newCompanyId) {
            this.selectedCompanyId = newCompanyId;
        },
        handleLoadingChanged(newLoading) {
            this.loading = newLoading;
        },
        toggleFloatingMenu() {
            this.showFloatingMenu = !this.showFloatingMenu;
            // Cerrar el menú cuando se hace clic fuera
            if (this.showFloatingMenu) {
                document.addEventListener('click', this.handleClickOutside);
            } else {
                document.removeEventListener('click', this.handleClickOutside);
            }
        },
        handleClickOutside(event) {
            const floatingBtn = this.$el.querySelector('.floating-btn');
            if (!floatingBtn.contains(event.target)) {
                this.showFloatingMenu = false;
                document.removeEventListener('click', this.handleClickOutside);
            }
        },
        handleScreenChange(screen) {
            this.setScreenActive(screen);
            this.showFloatingMenu = false;
            document.removeEventListener('click', this.handleClickOutside);
        },
        ...mapActions("usabilityEvents", ["createUsabilityEvents"]),
    },
    computed: {
        ...mapState("dashboard", ["screens", "dataDash1", "dataDash2"]),
    }
};
</script>

<style scoped>
/* Aplicar Poppins a todo el contenedor del dashboard */
.dashboard-container {
    font-family: 'Poppins', sans-serif;
}

/* Estilos para el botón flotante */
.floating-btn {
    position: fixed;
    bottom: 3.3rem;
    right: 2rem;
    z-index: 1050;
}

.btn-circle.btn-lg {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: #0FBEF9;
    border: none;
    box-shadow: 0 4px 15px rgba(15, 190, 249, 0.3);
}

.btn-circle.btn-lg:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 20px rgba(15, 190, 249, 0.4);
    background: #0FBEF9;
}

.btn-circle.btn-lg.active {
   
    background: #0FBEF9;
}

.btn-circle.btn-lg i {
    font-size: 1.5rem;
    animation-play-state: paused;
}

.btn-circle.btn-lg:hover i {
    animation-play-state: running;
}


/* Efecto de pulso */
.shadow-pulse {
    animation: pulse 2s infinite;
}

@keyframes pulse {
    0% {
        box-shadow: 0 0 0 0 rgba(15, 190, 249, 0.4);
    }
    70% {
        box-shadow: 0 0 0 10px rgba(15, 190, 249, 0);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(15, 190, 249, 0);
    }
}

/* Estilos para el menú flotante */
.floating-menu {
    position: absolute;
    bottom: 80px;
    right: 0;
    background: white;
    border-radius: 15px;
    padding: 1rem;
    min-width: 220px;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
}

.btn-menu {
    width: 100%;
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    margin-bottom: 0.5rem;
    color: #6c757d;
    background-color: #f8f9fa;
    border: none;
    border-radius: 10px;
    transition: all 0.3s ease;
}

.btn-menu:last-child {
    margin-bottom: 0;
}

.btn-menu:hover,
.btn-menu.active {
    color: white;
    background: #0FBEF9;
    transform: translateX(-5px);
}

.btn-menu i {
    margin-right: 1rem;
    font-size: 1.2rem;
    width: 20px;
    text-align: center;
}

.btn-menu span {
    font-weight: 500;
}

/* Transiciones */
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.3s, transform 0.3s;
}

.fade-enter,
.fade-leave-to {
    opacity: 0;
    transform: translateY(10px);
}

/* Mantener otros estilos existentes */
.equal-height {
    display: flex;
    flex-wrap: wrap;
}

.equal-height>[class*="col-"] {
    display: flex;
    flex-direction: column;
}

.equal-height .card,
.equal-height .RiskInventory,
.equal-height .PqRegulation,
.equal-height .RiskConcentration,
.equal-height .RiskAnalysis {
    flex: 1 1 auto;
    display: flex;
    flex-direction: column;
}

.card,
.RiskInventory,
.PqRegulation,
.RiskConcentration,
.RiskAnalysis {
    width: 100%;
}
</style>